import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearch, faPlus } from '@fortawesome/free-solid-svg-icons';
import adminFacilitatorService from '../../services/adminFacilitatorService';
import { toast } from 'react-toastify';

const CreateFacilitatorModal = ({ onClose, onSuccess, specializations }) => {
  const [step, setStep] = useState(1); // 1 = search user, 2 = configure
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Facilitator config
  const [isActive, setIsActive] = useState(true);
  const [selectedSpecializations, setSelectedSpecializations] = useState([]);
  const [maxActiveMatches, setMaxActiveMatches] = useState(10);
  const [bio, setBio] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.warning('Please enter a search term');
      return;
    }

    setSearching(true);
    try {
      const response = await adminFacilitatorService.searchUsers(searchQuery);
      setSearchResults(response.users || []);
      if (response.users.length === 0) {
        toast.info('No users found');
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectUser = (user) => {
    if (user.is_facilitator) {
      toast.warning('This user is already a facilitator');
      return;
    }
    setSelectedUser(user);
    setStep(2);
  };

  const toggleSpecialization = (spec) => {
    if (selectedSpecializations.includes(spec)) {
      setSelectedSpecializations(selectedSpecializations.filter(s => s !== spec));
    } else {
      setSelectedSpecializations([...selectedSpecializations, spec]);
    }
  };

  const handleSubmit = async () => {
    if (selectedSpecializations.length === 0) {
      toast.warning('Please select at least one specialization');
      return;
    }

    if (maxActiveMatches < 1 || maxActiveMatches > 50) {
      toast.warning('Max active matches must be between 1 and 50');
      return;
    }

    setSubmitting(true);
    try {
      await adminFacilitatorService.createFacilitator({
        user_id: selectedUser.id,
        is_active: isActive,
        specializations: selectedSpecializations,
        max_active_matches: maxActiveMatches,
        bio: bio.trim()
      });
      toast.success('Facilitator created successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create facilitator');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '12px',
        maxWidth: '700px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        border: '1px solid #333'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #333',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          backgroundColor: '#1a1a1a',
          zIndex: 1
        }}>
          <h2 style={{ color: '#FFB6C1', fontSize: '1.5rem', margin: 0 }}>
            {step === 1 ? 'Search User' : 'Configure Facilitator'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#999',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem' }}>
          {step === 1 ? (
            // Step 1: Search User
            <>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#FFB6C1',
                  fontWeight: '600'
                }}>
                  Search for User
                </label>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Enter name, email, or username..."
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      backgroundColor: '#000',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '1rem'
                    }}
                  />
                  <button
                    onClick={handleSearch}
                    disabled={searching}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#FFB6C1',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#000',
                      fontWeight: '600',
                      cursor: searching ? 'not-allowed' : 'pointer',
                      opacity: searching ? 0.7 : 1
                    }}
                  >
                    <FontAwesomeIcon icon={faSearch} /> {searching ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div>
                  <h3 style={{ color: '#ccc', marginBottom: '1rem', fontSize: '1.1rem' }}>
                    Search Results
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {searchResults.map(user => (
                      <div
                        key={user.id}
                        onClick={() => !user.is_facilitator && handleSelectUser(user)}
                        style={{
                          backgroundColor: '#000',
                          border: `1px solid ${user.is_facilitator ? '#666' : '#333'}`,
                          borderRadius: '8px',
                          padding: '1rem',
                          cursor: user.is_facilitator ? 'not-allowed' : 'pointer',
                          opacity: user.is_facilitator ? 0.5 : 1,
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (!user.is_facilitator) {
                            e.currentTarget.style.borderColor = '#FFB6C1';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!user.is_facilitator) {
                            e.currentTarget.style.borderColor = '#333';
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <div style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '50%',
                            backgroundColor: '#FFB6C1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.5rem',
                            fontWeight: '700',
                            color: '#000'
                          }}>
                            {user.full_name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: '#FFB6C1', fontWeight: '600', marginBottom: '0.25rem' }}>
                              {user.full_name}
                            </div>
                            <div style={{ color: '#999', fontSize: '0.9rem' }}>
                              {user.email}
                            </div>
                            <div style={{ color: '#666', fontSize: '0.85rem' }}>
                              @{user.username}
                            </div>
                          </div>
                          {user.is_facilitator && (
                            <div style={{
                              padding: '0.375rem 0.75rem',
                              backgroundColor: '#10b98120',
                              border: '1px solid #10b981',
                              borderRadius: '20px',
                              color: '#10b981',
                              fontSize: '0.85rem',
                              fontWeight: '600'
                            }}>
                              Already Facilitator
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            // Step 2: Configure Facilitator
            <>
              {/* Selected User */}
              <div style={{
                backgroundColor: '#000',
                border: '1px solid #333',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    backgroundColor: '#FFB6C1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    color: '#000'
                  }}>
                    {selectedUser.full_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <div style={{ color: '#FFB6C1', fontWeight: '600' }}>
                      {selectedUser.full_name}
                    </div>
                    <div style={{ color: '#999', fontSize: '0.9rem' }}>
                      {selectedUser.email}
                    </div>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ color: '#ccc' }}>
                    Active (can accept new match requests)
                  </span>
                </label>
              </div>

              {/* Specializations */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.75rem',
                  color: '#FFB6C1',
                  fontWeight: '600'
                }}>
                  Specializations * (Select all that apply)
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '0.75rem'
                }}>
                  {specializations.map(spec => (
                    <label
                      key={spec}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem',
                        backgroundColor: selectedSpecializations.includes(spec) ? '#FFB6C120' : '#000',
                        border: `1px solid ${selectedSpecializations.includes(spec) ? '#FFB6C1' : '#333'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSpecializations.includes(spec)}
                        onChange={() => toggleSpecialization(spec)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{ 
                        color: selectedSpecializations.includes(spec) ? '#FFB6C1' : '#ccc',
                        fontSize: '0.9rem',
                        textTransform: 'capitalize'
                      }}>
                        {spec.replace('_', ' ')}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Max Active Matches */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#FFB6C1',
                  fontWeight: '600'
                }}>
                  Maximum Active Matches
                </label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={maxActiveMatches}
                  onChange={(e) => setMaxActiveMatches(parseInt(e.target.value) || 10)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: '#000',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '1rem'
                  }}
                />
                <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#999' }}>
                  How many buddies can this facilitator support simultaneously?
                </p>
              </div>

              {/* Bio */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#FFB6C1',
                  fontWeight: '600'
                }}>
                  Bio (Optional)
                </label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Write a brief bio for this facilitator..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: '#000',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '1rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={() => setStep(1)}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    backgroundColor: '#000',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: '#ccc',
                    cursor: 'pointer'
                  }}
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    backgroundColor: '#FFB6C1',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#000',
                    fontWeight: '600',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? 0.7 : 1
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> {submitting ? 'Creating...' : 'Create Facilitator'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateFacilitatorModal;
